/**
 * Single source of truth for appointment type and status presentation.
 *
 * These values MUST match the backend `Appointment.Type` and
 * `Appointment.Status` enums. The list used to be duplicated as two literals
 * in Appointments.jsx and BookAppointmentModal.jsx, in different orders — and
 * an earlier divergence (a 'SALES' value that was never in the enum, used as
 * the modal's default) meant every booking made from that screen failed to
 * bind on the server. One list, imported by both, so that cannot recur.
 *
 * Colour policy: TYPE drives the card fill, STATUS drives the left border.
 * A dealer scans the calendar for "what kind of appointment", not "what state
 * is it in" — and test drives and deliveries being indistinguishable was the
 * specific complaint from the September showcase.
 */

export const APPT_TYPES = [
  { value: 'TEST_DRIVE',      label: 'Test drive',           short: 'Drive',    color: 'var(--accent-primary)', icon: '🚗', stream: 'SALES' },
  { value: 'DELIVERY',        label: 'Delivery',             short: 'Delivery', color: 'var(--success)',        icon: '🔑', stream: 'SALES' },
  { value: 'SHOWROOM_VISIT',  label: 'Showroom visit',       short: 'Visit',    color: 'var(--info)',           icon: '🏢', stream: 'SALES' },
  { value: 'APPRAISAL',       label: 'Trade-in appraisal',   short: 'Trade',    color: 'var(--warning)',        icon: '📋', stream: 'SALES' },
  { value: 'FINANCE_CONSULT', label: 'Finance consultation', short: 'Finance',  color: '#8b5cf6',               icon: '💷', stream: 'SALES' },
  { value: 'SERVICE',         label: 'Service',              short: 'Service',  color: 'var(--text-tertiary)',  icon: '🔧', stream: 'SERVICE' },
  { value: 'FOLLOW_UP_CALL',  label: 'Follow-up call',       short: 'Call',     color: 'var(--text-muted)',     icon: '📞', stream: 'SALES' },
];

export const APPT_TYPE_VALUES = APPT_TYPES.map((t) => t.value);

const TYPE_BY_VALUE = Object.fromEntries(APPT_TYPES.map((t) => [t.value, t]));

/** Never returns undefined — an unknown value degrades to readable text. */
export function typeMeta(value) {
  return TYPE_BY_VALUE[value] || {
    value,
    label: String(value || '').replace(/_/g, ' ') || 'Appointment',
    short: String(value || '').replace(/_/g, ' '),
    color: 'var(--text-muted)',
    icon: '•',
    stream: 'SALES',
  };
}

export const typeLabel = (v) => typeMeta(v).label;
export const typeColor = (v) => typeMeta(v).color;

/**
 * Types belonging to the sales workflow. Service must not appear on a sales
 * surface (segregation), so the sales calendar defaults to excluding it.
 */
export const SALES_TYPES = APPT_TYPES.filter((t) => t.stream === 'SALES').map((t) => t.value);
export const SERVICE_TYPES = APPT_TYPES.filter((t) => t.stream === 'SERVICE').map((t) => t.value);

export const APPT_STATUSES = [
  { value: 'REQUESTED',   label: 'Requested',   color: 'var(--warning)' },
  { value: 'CONFIRMED',   label: 'Confirmed',   color: 'var(--info)' },
  { value: 'CHECKED_IN',  label: 'Checked in',  color: 'var(--accent-primary)' },
  { value: 'COMPLETED',   label: 'Completed',   color: 'var(--success)' },
  { value: 'NO_SHOW',     label: 'No show',     color: 'var(--danger)' },
  { value: 'CANCELLED',   label: 'Cancelled',   color: 'var(--danger)' },
  { value: 'RESCHEDULED', label: 'Rescheduled', color: 'var(--accent-primary)' },
];

export const APPT_STATUS_VALUES = APPT_STATUSES.map((s) => s.value);

const STATUS_BY_VALUE = Object.fromEntries(APPT_STATUSES.map((s) => [s.value, s]));

export function statusMeta(value) {
  return STATUS_BY_VALUE[value] || {
    value,
    label: String(value || '').replace(/_/g, ' ') || 'Unknown',
    color: 'var(--text-muted)',
  };
}

export const statusLabel = (v) => statusMeta(v).label;
export const statusColor = (v) => statusMeta(v).color;

/** Statuses that mean the appointment is no longer live. */
export const CLOSED_STATUSES = ['COMPLETED', 'CANCELLED', 'NO_SHOW'];
export const isClosedAppt = (a) => CLOSED_STATUSES.includes(a?.status);

/**
 * Saved filter presets. The first two are the views named directly in the
 * September showcase — "I want test drives AND just my deliveries" — so they
 * ship by default rather than each dealer rebuilding them.
 */
export const CALENDAR_PRESETS = [
  {
    id: 'drives-deliveries',
    label: 'Test drives + deliveries',
    filters: { types: ['TEST_DRIVE', 'DELIVERY'], statuses: [], consultantIds: [] },
  },
  {
    id: 'deliveries',
    label: 'Deliveries only',
    filters: { types: ['DELIVERY'], statuses: [], consultantIds: [] },
  },
  {
    id: 'unconfirmed',
    label: 'Needs confirming',
    filters: { types: [], statuses: ['REQUESTED'], consultantIds: [] },
  },
];

/**
 * Consultant colours for the diary. Each consultant gets one colour so a busy
 * calendar can be read by person. 24 hues are spread evenly round the colour
 * wheel, alternating lighter and darker so neighbouring hues stay apart.
 */
const PALETTE_SIZE = 24;
export const CONSULTANT_PALETTE = Array.from({ length: PALETTE_SIZE }, (_, i) => {
  const hue = Math.round((i * 360) / PALETTE_SIZE);
  const light = i % 2 === 0 ? 42 : 58;
  return `hsl(${hue} 70% ${light}%)`;
});
export const UNASSIGNED_COLOR = 'var(--text-muted)';

/** FNV-1a hash of the id → preferred palette slot. */
function slotFor(id) {
  let h = 0x811c9dc5;
  const str = String(id);
  for (let k = 0; k < str.length; k++) {
    h ^= str.charCodeAt(k);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h % PALETTE_SIZE;
}

/**
 * Assigns every consultant id a colour, no two sharing one (up to 24).
 * Each id starts at the slot its hash points to; if that is taken it steps to
 * the next free slot. Ids are handled in sorted order so the result is the
 * same on every load. A consultant's colour only moves when an id that sorts
 * before theirs collides with their preferred slot.
 */
export function assignConsultantColors(ids) {
  const sorted = [...new Set(ids.map(String))].sort();
  const taken = new Set();
  const out = new Map();
  sorted.forEach((id) => {
    let slot = slotFor(id);
    if (taken.size < PALETTE_SIZE) {
      while (taken.has(slot)) slot = (slot + 1) % PALETTE_SIZE;
      taken.add(slot);
    }
    out.set(id, CONSULTANT_PALETTE[slot]);
  });
  return out;
}
